import { FormField } from "common";
import { useFormikContext } from "formik";
import { uniqueId } from "lodash";
import React, { useState } from "react";
import { Purchase } from "types";
import { numberFormat } from "../../../utils/numbers";
import { RadioInput } from "../../Common/RadioInput";
import { CurrencyInputField } from "../../Field/CurrencyInputField";
import { SplitPayDemographics, SplitPayFormValues, SplitPayOptions } from "./CreatePurchaseSplitPayTypes";
import { getDivideAndPayAmount } from "./helpers";
import { SplitPayDivideAndAdd } from "./SplitPayDivideAndAdd";
import { SplitPaySelectDemographics } from "./SplitPaySelectDemographics";

interface SplitPayOptionFieldsProps {
    purchase: Purchase;
    splitPayDemographics: SplitPayDemographics;
    onSplitPayDemographicsChange: (splitPayDemographics: SplitPayDemographics) => void;
}

export const SplitPayOptionFields = ({
    purchase,
    onSplitPayDemographicsChange,
    splitPayDemographics,
}: SplitPayOptionFieldsProps) => {
    const { amount, balance } = purchase;
    const { setFieldValue, values } = useFormikContext<SplitPayFormValues>();
    const { splitPayOption, divideParts, payParts } = values;
    const [calculatedAmount, setCalculatedAmount] = useState(0);
    const divideAndPayAmount = getDivideAndPayAmount(amount, divideParts, payParts, purchase);

    const popoverParentDivideOptionId = uniqueId("popoverParentId");
    const popoverParentDemographicsOptionId = uniqueId("popoverParentId");

    const handleClose = () => {
        setFieldValue("amount", 0);
    };

    const handleCustomAmountSelection = () => {
        setFieldValue("amount", 0);
        setFieldValue("splitPayOption", SplitPayOptions.CUSTOM_AMOUNT);
    };

    const handleDividePartSelection = () => {
        const amount = payParts > 0 ? numberFormat(divideAndPayAmount) : 0;
        setFieldValue("amount", amount);
        setFieldValue("splitPayOption", SplitPayOptions.DIVIDE_PARTS);
    };

    const handleDivideAndPaySubmit = () => {
        let amount = divideAndPayAmount > balance ? balance : divideAndPayAmount;
        amount = payParts > 0 ? amount : 0;
        setFieldValue("amount", amount);
    };

    const handleDemographicSelection = () => {
        const amount = calculatedAmount > 0 ? calculatedAmount : 0;
        setFieldValue("amount", amount);
        setFieldValue("splitPayOption", SplitPayOptions.SELECT_DEMOGRAPHIC_AND_ADDON);
    };

    const handlePayRemainingBalance = () => {
        const amount = balance > 0 ? balance : 0;
        setFieldValue("amount", amount);
        setFieldValue("splitPayOption", SplitPayOptions.REMAINING_BALANCE);
    };

    const handleSelectDemographicSubmit = (amount: number) => {
        if (amount > purchase.balance) {
            setFieldValue("amount", balance);
            setCalculatedAmount(balance);
        } else {
            setFieldValue("amount", amount);
            setCalculatedAmount(amount);
        }
    };

    return (
        <>
            <FormField name="splitPaymentField" label="Split Payment Option" className="space-y-2">
                <RadioInput
                    checked={splitPayOption === SplitPayOptions.SELECT_DEMOGRAPHIC_AND_ADDON}
                    onChange={handleDemographicSelection}
                >
                    <div id={popoverParentDemographicsOptionId}>
                        Pay for
                        <SplitPaySelectDemographics
                            splitPayDemographics={splitPayDemographics}
                            purchase={purchase}
                            popoverParentId={popoverParentDemographicsOptionId}
                            onSplitPayDemographicsChange={onSplitPayDemographicsChange}
                            onSubmit={handleSelectDemographicSubmit}
                            onClose={handleClose}
                        />
                    </div>
                </RadioInput>
                <RadioInput
                    checked={splitPayOption === SplitPayOptions.DIVIDE_PARTS}
                    onChange={handleDividePartSelection}
                >
                    <div id={popoverParentDivideOptionId}>
                        Divide total evenly by
                        <SplitPayDivideAndAdd
                            purchase={purchase}
                            popoverParentId={popoverParentDivideOptionId}
                            onSubmit={handleDivideAndPaySubmit}
                            onClose={handleClose}
                        />
                    </div>
                </RadioInput>
                <RadioInput
                    checked={splitPayOption === SplitPayOptions.REMAINING_BALANCE}
                    onChange={handlePayRemainingBalance}
                >
                    Pay Remaining Balance
                </RadioInput>
                <RadioInput
                    checked={splitPayOption === SplitPayOptions.CUSTOM_AMOUNT}
                    onChange={handleCustomAmountSelection}
                >
                    Custom Amount
                </RadioInput>
            </FormField>

            {splitPayOption === SplitPayOptions.CUSTOM_AMOUNT && (
                <FormField name="amount" label="Amount">
                    <CurrencyInputField />
                </FormField>
            )}
        </>
    );
};
